'use client';

import { CmsOffer } from '@hyphengroup/cms-components/types/cmsProduct';
import { useEffect, useRef, useState } from 'react';
import { BundleCard } from '$/(cms)/-cms/apply-v3/[[...slugs]]/components/BundleCard';
import {
  BundleUpsellContext,
  trackBundleDetails,
  trackBundleImpression,
  trackBundleSelection,
} from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/bundleTracking';
import { getBundleOfferId } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/helpers';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';

type Props = {
  offers: CmsOffer[];
  product?: CmsApplyRewardProduct;
  trackingContext?: BundleUpsellContext;
  onSelectionChange?: (offer: CmsOffer | undefined, index?: number) => void;
};

export function BundleCardGroup({
  offers,
  product,
  trackingContext,
  onSelectionChange,
}: Props) {
  const [expandedOfferId, setExpandedOfferId] = useState<string>();
  const [selectedOfferId, setSelectedOfferId] = useState<string>();
  const impressionIdsRef = useRef(new Set<string>());

  useEffect(() => {
    offers.forEach((offer, index) => {
      const offerId = getBundleOfferId(offer);
      if (impressionIdsRef.current.has(offerId)) return;

      trackBundleImpression(offer, index);
      impressionIdsRef.current.add(offerId);
    });
  }, [offers]);

  const selectOffer = (offer: CmsOffer, position: number) => {
    const offerId = getBundleOfferId(offer);
    setSelectedOfferId(offerId);
    onSelectionChange?.(offer, position);

    if (product && trackingContext) {
      trackBundleSelection(product, offer, position, trackingContext);
    }
  };

  const toggleExpanded = (offer: CmsOffer, position: number) => {
    const offerId = getBundleOfferId(offer);
    setExpandedOfferId((current) =>
      current === offerId ? undefined : offerId,
    );

    if (expandedOfferId !== offerId) {
      if (product) {
        trackBundleDetails(product, offer, position);
      }
    }
  };

  if (offers.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-spacing/gap/y">
      {offers.map((offer, index) => {
        const offerId = getBundleOfferId(offer);
        const position = index;
        return (
          <BundleCard
            expanded={expandedOfferId === offerId}
            key={offerId}
            offer={offer}
            position={position}
            selected={selectedOfferId === offerId}
            onSelect={selectOffer}
            onToggleExpanded={toggleExpanded}
          />
        );
      })}
    </div>
  );
}
